import React from 'react';
import { Stack, Typography } from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import DirectionsWalkOutlinedIcon from '@mui/icons-material/DirectionsWalkOutlined';
import { useTranslation } from 'react-i18next';
import type { TutorSessionType } from '../../models/Tutor';
import {
  normalizeStudentLessonCities,
  normalizeTutorSessionTypes,
  supportsAtStudentPlace,
  supportsOfflineSession,
} from '../../utils/tutorSessionType';
import TutorLessonSessionTypeChip from './TutorLessonSessionTypeChip';

export interface TutorSessionDiscoveryMetaProps {
  supportedSessionTypes?: TutorSessionType[] | null;
  /** Preformatted lesson location line (e.g. from attached space address). */
  locationSummary?: string | null;
  /**
   * Cities for `AT_STUDENT` lessons from the API.
   * Do not pass the attached space city here.
   */
  studentLessonCities?: string[] | null;
}

const TutorSessionDiscoveryMeta: React.FC<TutorSessionDiscoveryMetaProps> = ({
  supportedSessionTypes,
  locationSummary,
  studentLessonCities,
}) => {
  const { t } = useTranslation();
  const sessionTypes = normalizeTutorSessionTypes(supportedSessionTypes);
  const addressSummary =
    supportsOfflineSession(sessionTypes) && locationSummary?.trim() ? locationSummary.trim() : null;
  const studentCities =
    supportsAtStudentPlace(sessionTypes) ? normalizeStudentLessonCities(studentLessonCities) : [];
  const studentCitiesSummary = studentCities.length > 0 ? studentCities.join(', ') : null;

  if (sessionTypes.length === 0 && !addressSummary && !studentCitiesSummary) {
    return null;
  }

  return (
    <Stack spacing={0.75}>
      {addressSummary ? (
        <Stack direction="row" spacing={0.5} alignItems="flex-start">
          <PlaceOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.15 }} />
          <Typography variant="body2" color="text.secondary">
            {addressSummary}
          </Typography>
        </Stack>
      ) : null}
      {studentCitiesSummary ? (
        <Stack direction="row" spacing={0.5} alignItems="flex-start">
          <DirectionsWalkOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.15 }} />
          <Typography variant="body2" color="text.secondary">
            {t('tutorSearch.studentPlaceCities', {
              cities: studentCitiesSummary,
              defaultValue: 'Visits students in {{cities}}',
            })}
          </Typography>
        </Stack>
      ) : null}
      {sessionTypes.length > 0 ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          {sessionTypes.map((type) => (
            <TutorLessonSessionTypeChip key={type} sessionType={type} />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default TutorSessionDiscoveryMeta;
